import React, { createContext, useMemo, useEffect, useState, useContext, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useIntl, useToggle, useFileFavorites, useSelectable, useFileDownload } from 'hooks'
import { PATIENT_FILE_FILTERS } from 'app/constants'
import { CollectionContext } from 'features/surgical-cloud/PatientsCollections'
import { favoriteSelector } from 'features/favorite/favoriteSlice'
import { set } from 'features/file-view/fileSlice'
import Share from 'features/share/Share'
import Tags from 'features/tags'
import { Loader } from 'components/Loaders'
import CollectionDetailsHeader from 'components/Collection/CollectionDetailsHeader'
import CollectionDetailsFallback from 'components/Fallback/Details'
import FilesFilter from 'components/FilesUIFilter'
import SelectedFileActions from 'components/SelectedFileActions'
import ThumbnailsContainer from './ThumbnailsContainer'
import { Checkbox, ScrollableDirective } from 'components/Zui'

export const FileContext = createContext(null)

const Details = () => {
    const { t } = useIntl()
    const history = useHistory()
    const reduxDispatch = useDispatch()
    const [filter, setFilter] = useState(null)
    const [shareableCollection, setShareableCollection] = useState()
    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useToggle()
    const { collectionId } = useContext(CollectionContext)
    const data = useSelector(state => favoriteSelector.selectAll(state))
    const { download } = useFileDownload('files')

    const { actions, dispatch, selectable, handleSelectFile, handleSelectAll, isSelectedAll } =
        useSelectable(data, filter)

    const { summary, latestDateTime, favoriteFileIds, toggleAllFavorite, toggleFavorite } =
        useFileFavorites(undefined, selectable.selectedFiles)

    useEffect(() => {
        setFilter('all')
        dispatch(actions.unSelectAll())
    }, [collectionId])

    const sortedData = useMemo(
        () => data.sort((a, b) => new Date(b.updatedDateTime) - new Date(a.updatedDateTime)),
        [data]
    )

    const getSingleFileType = useCallback(() => {
        if (selectable.selectedFiles.length > 1) return
        const fileId = selectable.selectedFiles[0]
        return data?.find(f => f.fileId === fileId).fileType.toLowerCase()
    }, [selectable, data])

    const getFilteredFileIds = useCallback(() => {
        let fileIds = []
        if (filter !== 'all') {
            fileIds = data?.filter(t => t.fileType === filter).map(t => t.fileId) || []
        } else {
            fileIds = data?.map(f => f.fileId) || []
        }
        return fileIds
    }, [filter, data])

    const handleAllFavorite = () => {
        toggleAllFavorite(false)
    }

    const handleShareFiles = () => {
        setShareableCollection({
            type: 'files',
            fileIds: selectable.selectedFiles,
            fileType: getSingleFileType(),
            name:
                selectable.selectedFiles.length > 1
                    ? selectable.selectedFiles.length + ' files'
                    : null,
            collection: {
                name: 'All Favorites',
            },
        })
        onShareOpen()
    }

    const handleDownloadFiles = () => {
        download({
            fileIds: selectable.selectedFiles,
        })
    }

    const handleFileView = useCallback(
        id => {
            const fileIds = getFilteredFileIds()
            const viewFiles = data?.filter(f => fileIds.includes(f.fileId)) || []

            reduxDispatch(set(viewFiles))
            history.push({
                pathname: '/files/' + id,
                key: id,
                state: {
                    actionType: 'file-view',
                    tab: 'collections',
                    id: 'favorites',
                    filter,
                },
            })
        },
        [filter, data]
    )

    const filterOptions = PATIENT_FILE_FILTERS.map(op => {
        return { ...op, count: summary[op.value] || 0 }
    })

    const RenderThumbnailsContainer = useMemo(
        () => (
            <div className="collection-item-grid-view-wrapper">
                <ScrollableDirective
                    style={{
                        maxHeight: '100%',
                        width: '100%',
                        flexGrow: '1',
                        overflow: 'auto',
                    }}
                >
                    <div className="collection-item-grid-view-container">
                        <div className="header-wrap">
                            <div className="accordion-data-wrapper">
                                <div className="accordion-data-container">
                                    <ThumbnailsContainer
                                        files={sortedData}
                                        filter={filter}
                                        isLoading={false}
                                        handleFavorite={toggleFavorite}
                                        handleSelectFile={handleSelectFile}
                                        handleClick={handleFileView}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollableDirective>
            </div>
        ),
        [data, filter, favoriteFileIds]
    )

    return (
        <FileContext.Provider value={{ selectable, actions, dispatch }}>
            <div className="content-container">
                <div className="collection-details-wrapper">
                    {data.length > 0 ? (
                        <>
                            <div className="patient-contents-header">
                                <CollectionDetailsHeader
                                    data={{
                                        name: 'All favorites',
                                        fileCount: summary.all,
                                        updatedDateTime: latestDateTime,
                                    }}
                                />
                                <div className="filter-select-options-wrapper">
                                    <div className="files-filter-wrapper">
                                        <FilesFilter
                                            isDisabled={!Boolean(summary.all)}
                                            value={filter}
                                            onChange={setFilter}
                                            options={filterOptions}
                                        />
                                        <Checkbox
                                            style={{ display: ' flex', fontSize: ' 14px' }}
                                            theme="zbds-dark"
                                            name="selectAll"
                                            disabled={!Boolean(summary.all)}
                                            value={isSelectedAll}
                                            onChange={e => handleSelectAll(e.target.value)}
                                        >
                                            <span style={{ color: '#D1D9E5' }}>
                                                {t('select.all')}
                                            </span>
                                        </Checkbox>
                                    </div>
                                    {Boolean(
                                        selectable.selectedFiles.length && summary[filter]
                                    ) && (
                                        <SelectedFileActions
                                            actions={[
                                                {
                                                    icon: 'share',
                                                    onClick: handleShareFiles,
                                                },
                                                {
                                                    icon: 'favoriteFilled',
                                                    onClick: handleAllFavorite,
                                                },
                                                {
                                                    icon: 'tag',
                                                    onClick: onTagOpen,
                                                },
                                                {
                                                    icon: 'download',
                                                    onClick: handleDownloadFiles,
                                                },
                                            ]}
                                        />
                                    )}
                                </div>
                            </div>
                            {RenderThumbnailsContainer}
                        </>
                    ) : (
                        <CollectionDetailsFallback title={t('fallback.no.media')} icon="image" />
                    )}
                </div>
                {isShareOpen && <Share onClose={onShareClose} data={shareableCollection} />}
                {isTagOpen && (
                    <Tags
                        onClose={onTagClose}
                        data={{ type: 'files', fileIds: selectable.selectedFiles }}
                    />
                )}
            </div>
        </FileContext.Provider>
    )
}

export default React.memo(Details)
